import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Grid
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  QuestionAnswer as QuestionIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'

const QuizDetail = ({ userData, onBack, quizId, quizTitle }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [questionResults, setQuestionResults] = useState([])
  const [showQuestionDialog, setShowQuestionDialog] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [quizData, setQuizData] = useState(null)

  useEffect(() => {
    if (user && quizId) {
      loadQuizData()
    }
  }, [user, quizId])

  const loadQuizData = async () => {
    try {
      setLoading(true)
      
      // Quiz verilerini yükle
      const quizRef = doc(db, 'miniQuizzes', quizId)
      const quizDoc = await getDoc(quizRef)
      
      if (!quizDoc.exists()) {
        toast.error('Quiz bulunamadı')
        onBack()
        return
      }
      
      const quizData = quizDoc.data()
      setQuizData(quizData)
      
      const questions = quizData.questions || []
      if (questions.length === 0) {
        toast.error('Quiz soruları bulunamadı')
        onBack()
        return
      }
      
      // Öğrenci sonuçlarını yükle
      await loadStudentResults(questions)
      
    } catch (error) {
      console.error('Error loading quiz data:', error)
      toast.error('Quiz yüklenirken hata oluştu')
      onBack()
    } finally {
      setLoading(false)
    }
  }

  const loadStudentResults = async (questions) => {
    try {
      const resultsRef = collection(db, 'quizResults')
      const q = query(
        resultsRef,
        where('quizId', '==', quizId),
        where('teacherId', '==', user.uid)
      )
      const querySnapshot = await getDocs(q)
      
      const studentResults = []
      
      for (const resultDoc of querySnapshot.docs) {
        const resultData = resultDoc.data()
        
        // Öğrenci adını al
        const userRef = doc(db, 'users', resultData.studentId)
        const userDoc = await getDoc(userRef)
        const studentName = userDoc.exists() ? userDoc.data().username : 'Bilinmeyen Öğrenci'
        
        studentResults.push({
          studentId: resultData.studentId,
          studentName: studentName,
          userAnswers: resultData.userAnswers || [],
          completionDate: resultData.completionDate?.toDate() || new Date()
        })
      }
      
      // Soru sonuçlarını işle
      processQuestionResults(questions, studentResults)
      
    } catch (error) {
      console.error('Error loading student results:', error)
      toast.error('Öğrenci sonuçları yüklenirken hata oluştu')
    }
  }

  const processQuestionResults = (questions, studentResults) => {
    const results = []
    
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i]
      const questionText = question.question || ''
      const correctAnswer = question.correctAnswer || ''
      
      const correctResults = []
      const wrongResults = []
      
      // Her öğrencinin cevabını kontrol et
      for (const student of studentResults) {
        if (student.userAnswers && i < student.userAnswers.length) {
          const studentAnswer = student.userAnswers[i]
          if (correctAnswer === studentAnswer) {
            correctResults.push(student)
          } else {
            wrongResults.push(student)
          }
        }
      }
      
      results.push({
        questionNumber: i + 1,
        questionText: questionText,
        correctAnswer: correctAnswer,
        correctResults: correctResults,
        wrongResults: wrongResults
      })
    }
    
    setQuestionResults(results)
  }

  const handleQuestionDetail = (questionResult) => {
    setSelectedQuestion(questionResult)
    setShowQuestionDialog(true)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {quizTitle || 'Quiz Detayları'}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        ) : questionResults.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Quiz soruları bulunamadı
          </Alert>
        ) : (
          <>
            {/* Quiz Bilgileri */}
            {quizData && (
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                    Quiz Bilgileri
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Başlık:</strong> {quizData.title || 'Quiz'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Soru Sayısı:</strong> {questionResults.length}
                      </Typography>
                    </Grid>
                    {quizData.description && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Açıklama:</strong> {quizData.description}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Oluşturulma:</strong> {formatDate(quizData.createdDate?.toDate())}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Toplam Öğrenci:</strong> {quizData.studentIds?.length || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Soru Listesi */}
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              Soru Analizleri
            </Typography>
            
            <List>
              {questionResults.map((questionResult, index) => (
                <Card key={index} sx={{ mb: 2, borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    {/* Soru Başlığı ve Detay Butonu */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        Soru {questionResult.questionNumber}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleQuestionDetail(questionResult)}
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        Detay
                      </Button>
                    </Box>

                    {/* Soru Metni */}
                    <Typography variant="body1" color="text.primary" mb={2}>
                      {questionResult.questionText}
                    </Typography>

                    {/* Doğru Cevap */}
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      <strong>Doğru Cevap:</strong> {questionResult.correctAnswer}
                    </Typography>

                    {/* İstatistikler */}
                    <Box display="flex" flexWrap="wrap" gap={2}>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={`Doğru: ${questionResult.correctResults.length} kişi`}
                        color="success"
                        variant="outlined"
                      />
                      <Chip
                        icon={<CancelIcon />}
                        label={`Yanlış: ${questionResult.wrongResults.length} kişi`}
                        color="error"
                        variant="outlined"
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </List>
          </>
        )}
      </Container>

      {/* Soru Detay Dialog */}
      <Dialog
        open={showQuestionDialog}
        onClose={() => setShowQuestionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <QuestionIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Soru {selectedQuestion?.questionNumber} Detayları
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedQuestion && (
            <Box>
              {/* Soru Bilgileri */}
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {selectedQuestion.questionText}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" mb={3}>
                <strong>Doğru Cevap:</strong> {selectedQuestion.correctAnswer}
              </Typography>

              {/* Doğru Çözenler */}
              <Typography variant="h6" color="success.main" gutterBottom>
                Doğru Çözenler ({selectedQuestion.correctResults.length} kişi):
              </Typography>
              
              {selectedQuestion.correctResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  - Yok
                </Typography>
              ) : (
                <Box mb={3}>
                  {selectedQuestion.correctResults.map((student, idx) => {
                    const answer = student.userAnswers && 
                      selectedQuestion.questionNumber - 1 < student.userAnswers.length 
                      ? student.userAnswers[selectedQuestion.questionNumber - 1] 
                      : '-'
                    
                    return (
                      <Box key={student.studentId} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          • <strong>{student.studentName}</strong> - Cevap: {answer}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Yanlış Çözenler */}
              <Typography variant="h6" color="error.main" gutterBottom>
                Yanlış Çözenler ({selectedQuestion.wrongResults.length} kişi):
              </Typography>
              
              {selectedQuestion.wrongResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  - Yok
                </Typography>
              ) : (
                <Box>
                  {selectedQuestion.wrongResults.map((student, idx) => {
                    const answer = student.userAnswers && 
                      selectedQuestion.questionNumber - 1 < student.userAnswers.length 
                      ? student.userAnswers[selectedQuestion.questionNumber - 1] 
                      : '-'
                    
                    return (
                      <Box key={student.studentId} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          • <strong>{student.studentName}</strong> - Cevap: {answer}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQuestionDialog(false)}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </SpaceTheme>
  )
}

export default QuizDetail
